import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { ThemeColors } from "./Scene";
import type { Path } from "./Path";

type Tick = (now: number) => void;

/**
 * Per-theme atmospheric decorations. The close lane stays readable, while the
 * horizon and path sides get landmarks, motion, animals, homes, rivers, and
 * weather so each lesson feels like it belongs to a bigger living world.
 */
export function addThemeDecor(
  group: THREE.Group,
  path: Path,
  theme: ThemeColors,
  courseLength: number,
  opts: { skyClouds?: boolean } = {},
): Tick | null {
  const ticks: Tick[] = [];
  ticks.push(...addVastBackdrop(group, path, theme, courseLength, opts.skyClouds !== false));

  switch (theme.id) {
    case "forest":
      ticks.push(...addForestDecor(group, path, courseLength));
      break;
    case "jungle":
      ticks.push(...addJungleDecor(group, path, courseLength));
      break;
    case "snow":
      ticks.push(...addSnowDecor(group, path, courseLength));
      break;
    case "desert":
      ticks.push(...addDesertDecor(group, path, courseLength));
      break;
    case "space":
      ticks.push(...addSpaceDecor(group, path, courseLength));
      break;
    case "candy":
      ticks.push(...addCandyDecor(group, path, courseLength));
      break;
  }

  return ticks.length ? (now: number) => ticks.forEach((tick) => tick(now)) : null;
}

/* ── shared vast-world layer ───────────────────────────────────────────── */
function addVastBackdrop(group: THREE.Group, path: Path, theme: ThemeColors, len: number, skyClouds = true): Tick[] {
  // Survival arena (skyClouds=false) skips the along-the-path cloud clusters
  // — addSkyCourseClouds drops a cluster ~4.5m BELOW the path centerline,
  // which would float directly underneath the floating platform.
  addCloudBanks(group, path, len, theme.id === "space" || !skyClouds ? 0 : 48);
  if (theme.id !== "space" && skyClouds) addSkyCourseClouds(group, path, len);

  if (theme.id === "space") {
    addPlanetRange(group, path, len);
    return [];
  }

  addMountainRange(group, path, theme, len);
  addDistantHills(group, path, theme, len);
  addVillage(group, path, theme, len);
  if (theme.id !== "desert" && theme.id !== "candy") addRiver(group, path, theme, len);
  return [];
}

function addMountainRange(group: THREE.Group, path: Path, theme: ThemeColors, len: number) {
  const mat = new THREE.MeshStandardMaterial({
    color: theme.id === "snow" ? 0xdfe8f1 : theme.id === "desert" ? 0xbd7b4e : 0x5d7f70,
    roughness: 0.9,
    flatShading: true,
  });
  const snowMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.8 });
  for (let i = 0; i < 18; i++) {
    const s = (i / 17) * len;
    const side = i % 2 === 0 ? -1 : 1;
    const offset = side * (58 + n(i, 1) * 34);
    const base = path.positionAt(s, offset);
    const h = 11 + n(i, 2) * 16;
    const r = 11 + n(i, 3) * 12;
    const peak = new THREE.Mesh(new THREE.ConeGeometry(r, h, 4), mat);
    peak.position.set(base.x, h / 2 - 0.4, base.z);
    peak.rotation.y = Math.PI / 4 + n(i, 4) * Math.PI;
    peak.scale.z = 0.75 + n(i, 5) * 0.8;
    peak.receiveShadow = true;
    group.add(peak);

    if (theme.id === "snow" || h > 19) {
      const cap = new THREE.Mesh(new THREE.ConeGeometry(r * 0.42, h * 0.24, 4), snowMat);
      cap.position.set(base.x, h * 0.87 - 0.4, base.z);
      cap.rotation.copy(peak.rotation);
      cap.scale.z = peak.scale.z;
      group.add(cap);
    }
  }
}

function addDistantHills(group: THREE.Group, path: Path, theme: ThemeColors, len: number) {
  const mat = new THREE.MeshStandardMaterial({
    color:
      theme.id === "desert"
        ? 0xd9945e
        : theme.id === "snow"
          ? 0xe8eff6
          : theme.id === "candy"
            ? 0xffd1e8
            : theme.ground.clone().lerp(new THREE.Color(0xffffff), 0.12),
    roughness: 0.9,
  });
  for (let s = 0; s < len; s += 34) {
    for (const side of [-1, 1] as const) {
      if (n(s, side) > 0.72) continue;
      const pos = path.positionAt(s, side * (28 + n(s, 7) * 14));
      const hill = new THREE.Mesh(new THREE.SphereGeometry(6 + n(s, 8) * 5, 12, 8), mat);
      hill.position.set(pos.x, 1.2, pos.z);
      hill.scale.set(1.7, 0.35, 1.0 + n(s, 9));
      hill.receiveShadow = true;
      group.add(hill);
    }
  }
}

function addCloudBanks(group: THREE.Group, path: Path, len: number, count: number) {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.66,
    fog: false,
  });
  for (let i = 0; i < count; i++) {
    const cloud = new THREE.Group();
    const puffs = 3 + Math.floor(n(i, 12) * 3);
    for (let j = 0; j < puffs; j++) {
      const puff = new THREE.Mesh(new THREE.SphereGeometry(2.4 + n(i, j) * 2.2, 10, 8), mat);
      puff.position.set(j * 3.1, n(j, i) * 1.1, n(i + j, 3) * 1.5);
      puff.scale.y = 0.45;
      cloud.add(puff);
    }
    const s = n(i, 20) * len;
    const side = i % 2 === 0 ? -1 : 1;
    const pos = path.positionAt(s, side * (42 + n(i, 21) * 35));
    cloud.position.set(pos.x, 20 + n(i, 22) * 12, pos.z);
    cloud.rotation.y = n(i, 23) * Math.PI;
    group.add(cloud);
  }
}

function addSkyCourseClouds(group: THREE.Group, path: Path, len: number) {
  const highMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
    fog: false,
  });
  const lowMat = new THREE.MeshBasicMaterial({
    color: 0xe8f8ff,
    transparent: true,
    opacity: 0.54,
    depthWrite: false,
    fog: false,
  });

  for (let s = -10; s < len + 28; s += 11) {
    const center = buildCloudCluster(lowMat, 4 + Math.floor(n(s, 4) * 3), 2.1, 0.42);
    const centerPos = path.positionAt(THREE.MathUtils.clamp(s, 0, len), (n(s, 1) - 0.5) * 4.5);
    center.position.set(centerPos.x, centerPos.y - 4.6 - n(s, 2) * 2.2, centerPos.z);
    center.scale.setScalar(1.0 + n(s, 3) * 0.55);
    center.rotation.y = n(s, 5) * Math.PI * 2;
    group.add(center);

    for (const side of [-1, 1] as const) {
      const sideCloud = buildCloudCluster(highMat, 3 + Math.floor(n(s, side) * 3), 1.65, 0.48);
      const sidePos = path.positionAt(THREE.MathUtils.clamp(s + n(s, 8) * 7, 0, len), side * (8.5 + n(s, 9) * 7));
      sideCloud.position.set(sidePos.x, sidePos.y + 2.1 + n(s, 10) * 3.4, sidePos.z);
      sideCloud.scale.setScalar(0.78 + n(s, 11) * 0.58);
      sideCloud.rotation.y = n(s, 12) * Math.PI * 2;
      group.add(sideCloud);
    }
  }
}

function buildCloudCluster(
  mat: THREE.Material,
  puffs: number,
  radius: number,
  flatness: number,
): THREE.Group {
  const cloud = new THREE.Group();
  for (let i = 0; i < puffs; i++) {
    const puff = new THREE.Mesh(
      new THREE.SphereGeometry(radius * (0.8 + n(i, puffs) * 0.75), 10, 8),
      mat,
    );
    puff.position.set((i - puffs / 2) * radius * 1.35, n(i, 2) * radius * 0.35, (n(i, 3) - 0.5) * radius * 1.3);
    puff.scale.y = flatness;
    cloud.add(puff);
  }
  return cloud;
}

function addRiver(group: THREE.Group, path: Path, theme: ThemeColors, len: number) {
  const river = new THREE.Mesh(
    new THREE.PlaneGeometry(18, len * 0.82),
    new THREE.MeshStandardMaterial({
      color: theme.id === "snow" ? 0x8fd3ff : 0x22b8e8,
      roughness: 0.35,
      metalness: 0.08,
      transparent: true,
      opacity: 0.76,
    }),
  );
  const mid = path.positionAt(len * 0.5, -24);
  river.position.set(mid.x, 0.015, mid.z);
  river.rotation.x = -Math.PI / 2;
  river.rotation.y = path.yawAt(len * 0.5) + 0.18;
  group.add(river);
}

function addVillage(group: THREE.Group, path: Path, theme: ThemeColors, len: number) {
  if (theme.id === "space") return;
  const count = theme.id === "jungle" || theme.id === "desert" ? 7 : 10;
  for (let i = 0; i < count; i++) {
    const s = 30 + n(i, 30) * (len - 60);
    const side = i % 2 === 0 ? -1 : 1;
    const pos = path.positionAt(s, side * (18 + n(i, 31) * 10));
    const house = theme.id === "candy" ? buildCandyHouse(i) : buildHouse(theme, i);
    house.position.copy(pos);
    house.rotation.y = path.yawAt(s) + (side === 1 ? -Math.PI / 2 : Math.PI / 2);
    house.scale.setScalar(0.75 + n(i, 32) * 0.45);
    group.add(house);
  }
}

/* ── forest: trees, houses, rabbits, butterflies ──────────────────────── */
function addForestDecor(group: THREE.Group, path: Path, len: number): Tick[] {
  const movers: { mesh: THREE.Object3D; base: THREE.Vector3; phase: number }[] = [];
  for (let i = 0; i < 9; i++) {
    const bunny = buildRabbit();
    const s = 20 + n(i, 40) * (len - 40);
    const side = i % 2 === 0 ? -1 : 1;
    const base = path.positionAt(s, side * (9 + n(i, 41) * 4));
    bunny.position.copy(base);
    bunny.rotation.y = path.yawAt(s) + (side === 1 ? Math.PI : 0);
    group.add(bunny);
    movers.push({ mesh: bunny, base, phase: n(i, 42) * Math.PI * 2 });
  }
  return [
    (now) => {
      const t = now * 0.001;
      for (const m of movers) {
        m.mesh.position.y = Math.abs(Math.sin(t * 2.8 + m.phase)) * 0.25;
        m.mesh.rotation.z = Math.sin(t * 2 + m.phase) * 0.06;
      }
    },
  ];
}

/* ── jungle: dense palms, birds, monkeys ───────────────────────────────── */
function addJungleDecor(group: THREE.Group, path: Path, len: number): Tick[] {
  const ticks: Tick[] = [];
  const birds: Bird[] = [];
  for (let i = 0; i < 10; i++) birds.push(addBird(group, path, len, i, 0.45));
  const monkeys: Array<{ mesh: THREE.Object3D; phase: number }> = [];
  for (let i = 0; i < 7; i++) {
    const monkey = buildMonkey();
    const s = 25 + n(i, 51) * (len - 50);
    const side = i % 2 === 0 ? -1 : 1;
    monkey.position.copy(path.positionAt(s, side * (13 + n(i, 52) * 5)));
    monkey.rotation.y = path.yawAt(s) + Math.PI / 2;
    group.add(monkey);
    monkeys.push({ mesh: monkey, phase: n(i, 53) * Math.PI * 2 });
  }
  ticks.push((now) => {
    const t = now * 0.001;
    updateBirds(birds, t);
    for (const m of monkeys) m.mesh.rotation.z = Math.sin(t * 2 + m.phase) * 0.18;
  });
  return ticks;
}

/* ── snow: snowfall, pine forests, cabins, bears ───────────────────────── */
function addSnowDecor(group: THREE.Group, path: Path, len: number): Tick[] {
  const ticks: Tick[] = [];
  const snowGeo = new THREE.BufferGeometry();
  const count = 420;
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    positions[i * 3] = (n(i, 1) - 0.5) * 90;
    positions[i * 3 + 1] = n(i, 2) * 26;
    positions[i * 3 + 2] = n(i, 3) * len;
  }
  snowGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  group.add(
    new THREE.Points(
      snowGeo,
      new THREE.PointsMaterial({ color: 0xffffff, size: 0.18, transparent: true, opacity: 0.86 }),
    ),
  );
  for (let i = 0; i < 7; i++) {
    const bear = buildBear();
    const s = 26 + n(i, 60) * (len - 52);
    const side = i % 2 === 0 ? -1 : 1;
    bear.position.copy(path.positionAt(s, side * (12 + n(i, 61) * 5)));
    bear.rotation.y = path.yawAt(s);
    group.add(bear);
  }
  ticks.push(() => {
    const arr = snowGeo.attributes.position!.array as Float32Array;
    for (let i = 0; i < count; i++) {
      const yi = i * 3 + 1;
      arr[yi] = (arr[yi] ?? 0) - 0.035;
      if ((arr[yi] ?? 0) < 0) arr[yi] = 24;
    }
    snowGeo.attributes.position!.needsUpdate = true;
  });
  return ticks;
}

/* ── desert: dunes, cactus fields, oasis, camels ───────────────────────── */
function addDesertDecor(group: THREE.Group, path: Path, len: number): Tick[] {
  const camels: Array<{ mesh: THREE.Object3D; phase: number }> = [];
  for (let s = 12; s < len; s += 18) {
    for (const side of [-1, 1] as const) {
      if (n(s, side) > 0.62) continue;
      const cactus = buildCactus();
      cactus.position.copy(path.positionAt(s, side * (10 + n(s, 8) * 13)));
      group.add(cactus);
    }
  }
  const oasis = buildOasis();
  oasis.position.copy(path.positionAt(len * 0.56, -26));
  group.add(oasis);

  for (let i = 0; i < 5; i++) {
    const camel = buildCamel();
    const s = 35 + n(i, 70) * (len - 70);
    const side = i % 2 === 0 ? -1 : 1;
    camel.position.copy(path.positionAt(s, side * (14 + n(i, 71) * 6)));
    camel.rotation.y = path.yawAt(s) + Math.PI / 2;
    group.add(camel);
    camels.push({ mesh: camel, phase: n(i, 72) * Math.PI * 2 });
  }
  return [
    (now) => {
      const t = now * 0.001;
      for (const c of camels) c.mesh.position.y = Math.sin(t * 1.6 + c.phase) * 0.08;
    },
  ];
}

/* ── candy: candy river, lollipop woods, sweet homes ───────────────────── */
function addCandyDecor(group: THREE.Group, path: Path, len: number): Tick[] {
  addCandyRiver(group, path, len);
  for (let s = 10; s < len; s += 12) {
    for (const side of [-1, 1] as const) {
      if (n(s, side) > 0.72) continue;
      const candy = n(s, 1) > 0.5 ? buildLollipop() : buildCandyCane();
      candy.position.copy(path.positionAt(s, side * (10 + n(s, 2) * 11)));
      candy.rotation.y = n(s, 3) * Math.PI * 2;
      group.add(candy);
    }
  }
  return [];
}

/* ── space: stars, planets, asteroids, tiny robot spectators ───────────── */
function addSpaceDecor(group: THREE.Group, path: Path, len: number): Tick[] {
  const count = 260;
  const pos = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    pos[i * 3] = (n(i, 80) - 0.5) * 110;
    pos[i * 3 + 1] = 4 + n(i, 81) * 35;
    pos[i * 3 + 2] = n(i, 82) * len;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  const stars = new THREE.Points(
    geo,
    new THREE.PointsMaterial({ color: 0xffffff, size: 0.26, transparent: true, opacity: 0.92 }),
  );
  group.add(stars);
  const asteroids: THREE.Object3D[] = [];
  for (let i = 0; i < 16; i++) {
    const a = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.2 + n(i, 83) * 1.9, 0),
      new THREE.MeshStandardMaterial({ color: 0x8d8797, flatShading: true }),
    );
    const s = n(i, 84) * len;
    const side = i % 2 === 0 ? -1 : 1;
    a.position.copy(path.positionAt(s, side * (18 + n(i, 85) * 18)));
    a.position.y = 5 + n(i, 86) * 10;
    group.add(a);
    asteroids.push(a);
  }
  return [
    (now) => {
      const t = now * 0.001;
      stars.rotation.y = t * 0.02;
      for (let i = 0; i < asteroids.length; i++) {
        const a = asteroids[i]!;
        a.rotation.x += 0.006 + i * 0.0003;
        a.rotation.y += 0.004;
        a.position.y += Math.sin(t + i) * 0.002;
      }
    },
  ];
}

/* ── builders ──────────────────────────────────────────────────────────── */
function buildHouse(theme: ThemeColors, i: number): THREE.Group {
  const g = new THREE.Group();
  const wallColor = theme.id === "snow" ? 0xd7eef8 : theme.id === "desert" ? 0xf1c27d : 0xfff0c9;
  const roofColor = [0xef476f, 0x6ec5ff, 0xff8b3d, 0x9b5cf6][i % 4]!;
  const body = new THREE.Mesh(new SoftBox(3, 2.1, 2.7), mat(wallColor));
  body.position.y = 1.05;
  body.castShadow = true;
  g.add(body);
  const roof = new THREE.Mesh(new THREE.ConeGeometry(2.25, 1.3, 4), mat(roofColor));
  roof.position.y = 2.75;
  roof.rotation.y = Math.PI / 4;
  roof.castShadow = true;
  g.add(roof);
  const door = new THREE.Mesh(new SoftBox(0.65, 1.1, 0.08), mat(0x7a4a28));
  door.position.set(0, 0.55, 1.39);
  g.add(door);
  return g;
}

function buildCandyHouse(i: number): THREE.Group {
  const g = buildHouse(
    {
      id: "candy",
      sky: new THREE.Color(),
      skyHorizon: new THREE.Color(),
      fog: new THREE.Color(),
      ground: new THREE.Color(),
      pathLight: new THREE.Color(),
      pathDark: new THREE.Color(),
      hemiGround: new THREE.Color(),
      ambient: 1,
      sun: 1,
      showSun: true,
    },
    i,
  );
  g.scale.y = 1.15;
  return g;
}

function buildSimpleTree(trunkColor = 0x6f4a2a, leafColor = 0x2f7d3a, height = 4.5) {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.48, height, 8), mat(trunkColor));
  trunk.position.y = height / 2;
  trunk.castShadow = true;
  g.add(trunk);
  const leaves = new THREE.Mesh(
    new THREE.IcosahedronGeometry(1.7, 0),
    new THREE.MeshStandardMaterial({ color: leafColor, flatShading: true }),
  );
  leaves.position.y = height + 1.05;
  leaves.castShadow = true;
  g.add(leaves);
  return g;
}

function buildPalm() {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.46, 5.2, 7), mat(0x8a5a2b));
  trunk.position.y = 2.6;
  trunk.rotation.z = 0.13;
  trunk.castShadow = true;
  g.add(trunk);
  const leafMat = mat(0x1f8a3b);
  for (let i = 0; i < 6; i++) {
    const leaf = new THREE.Mesh(new SoftBox(0.45, 0.15, 3.2), leafMat);
    leaf.position.y = 5.25;
    leaf.rotation.y = (i / 6) * Math.PI * 2;
    leaf.rotation.x = 0.38;
    leaf.castShadow = true;
    g.add(leaf);
  }
  return g;
}

function buildPine() {
  const g = new THREE.Group();
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.32, 2.6, 7), mat(0x6f4a2a));
  trunk.position.y = 1.3;
  g.add(trunk);
  for (let i = 0; i < 3; i++) {
    const cone = new THREE.Mesh(new THREE.ConeGeometry(1.7 - i * 0.35, 2.0, 7), mat(0x2d7d44));
    cone.position.y = 2.2 + i * 1.2;
    cone.castShadow = true;
    g.add(cone);
  }
  const snow = new THREE.Mesh(new THREE.ConeGeometry(0.95, 0.7, 7), mat(0xffffff));
  snow.position.y = 5.0;
  g.add(snow);
  return g;
}

function buildRabbit() {
  const g = new THREE.Group();
  const white = mat(0xf2f2f2);
  const body = new THREE.Mesh(new SoftBox(0.8, 0.45, 1.0), white);
  body.position.y = 0.35;
  g.add(body);
  const head = new THREE.Mesh(new SoftBox(0.48, 0.45, 0.48), white);
  head.position.set(0, 0.62, 0.62);
  g.add(head);
  for (const x of [-0.16, 0.16]) {
    const ear = new THREE.Mesh(new SoftBox(0.12, 0.56, 0.12), white);
    ear.position.set(x, 1.08, 0.62);
    g.add(ear);
  }
  return g;
}

function buildBear() {
  const g = new THREE.Group();
  const fur = mat(0xececec);
  const body = new THREE.Mesh(new SoftBox(2.0, 1.0, 2.6), fur);
  body.position.y = 0.5;
  body.castShadow = true;
  g.add(body);
  const head = new THREE.Mesh(new SoftBox(1.1, 0.9, 1.0), fur);
  head.position.set(0, 0.9, 1.55);
  g.add(head);
  const nose = new THREE.Mesh(new SoftBox(0.18, 0.18, 0.18), mat(0x111111));
  nose.position.set(0, 0.86, 2.08);
  g.add(nose);
  return g;
}

function buildMonkey() {
  const g = new THREE.Group();
  const fur = mat(0x8a5a2b);
  const body = new THREE.Mesh(new SoftBox(0.75, 0.85, 0.55), fur);
  body.position.y = 1.0;
  g.add(body);
  const head = new THREE.Mesh(new SoftBox(0.62, 0.58, 0.58), fur);
  head.position.y = 1.65;
  g.add(head);
  const tail = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.06, 6, 16), fur);
  tail.position.set(0, 1.0, -0.48);
  g.add(tail);
  return g;
}

function buildCactus() {
  const g = new THREE.Group();
  const green = mat(0x2f7d3a);
  const trunk = new THREE.Mesh(new SoftBox(0.58, 2.7, 0.58), green);
  trunk.position.y = 1.35;
  trunk.castShadow = true;
  g.add(trunk);
  for (const side of [-1, 1] as const) {
    const arm = new THREE.Mesh(new SoftBox(0.42, 1.25, 0.42), green);
    arm.position.set(side * 0.58, 1.6 + (side === 1 ? 0.35 : -0.05), 0);
    g.add(arm);
  }
  return g;
}

function buildOasis() {
  const g = new THREE.Group();
  const water = new THREE.Mesh(new THREE.CylinderGeometry(5, 5.7, 0.12, 24), mat(0x25c7e8));
  water.position.y = 0.05;
  g.add(water);
  for (let i = 0; i < 5; i++) {
    const palm = buildPalm();
    palm.position.set(Math.cos(i) * 5.2, 0, Math.sin(i) * 3.8);
    palm.scale.setScalar(0.75);
    g.add(palm);
  }
  return g;
}

function buildCamel() {
  const g = new THREE.Group();
  const camel = mat(0xb98245);
  const body = new THREE.Mesh(new SoftBox(1.8, 0.8, 0.7), camel);
  body.position.y = 0.9;
  g.add(body);
  const hump = new THREE.Mesh(new SoftBox(0.7, 0.7, 0.7), camel);
  hump.position.set(-0.2, 1.45, 0);
  g.add(hump);
  const neck = new THREE.Mesh(new SoftBox(0.35, 1.0, 0.35), camel);
  neck.position.set(0.85, 1.35, 0);
  neck.rotation.z = -0.35;
  g.add(neck);
  const head = new THREE.Mesh(new SoftBox(0.55, 0.42, 0.42), camel);
  head.position.set(1.25, 1.75, 0);
  g.add(head);
  for (const x of [-0.65, 0.65]) {
    for (const z of [-0.2, 0.2]) {
      const leg = new THREE.Mesh(new SoftBox(0.22, 0.9, 0.22), camel);
      leg.position.set(x, 0.35, z);
      g.add(leg);
    }
  }
  return g;
}

function buildLollipop() {
  const g = new THREE.Group();
  const stick = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 3, 8), mat(0xffffff));
  stick.position.y = 1.5;
  g.add(stick);
  const candy = new THREE.Mesh(new THREE.SphereGeometry(0.85, 14, 10), mat(0xff5fac));
  candy.position.y = 3.2;
  g.add(candy);
  return g;
}

function buildCandyCane() {
  const g = new THREE.Group();
  const cane = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 3.5, 10), mat(0xfff7f7));
  cane.position.y = 1.75;
  g.add(cane);
  for (let i = 0; i < 6; i++) {
    const stripe = new THREE.Mesh(new SoftBox(0.48, 0.18, 0.48), mat(0xef476f));
    stripe.position.y = 0.45 + i * 0.5;
    stripe.rotation.y = i * 0.8;
    g.add(stripe);
  }
  return g;
}

function addCandyRiver(group: THREE.Group, path: Path, len: number) {
  const river = new THREE.Mesh(
    new THREE.PlaneGeometry(16, len * 0.75),
    new THREE.MeshStandardMaterial({ color: 0xff8bc4, transparent: true, opacity: 0.75 }),
  );
  const pos = path.positionAt(len * 0.5, -24);
  river.position.set(pos.x, 0.02, pos.z);
  river.rotation.x = -Math.PI / 2;
  river.rotation.y = path.yawAt(len * 0.5) - 0.16;
  group.add(river);
}

function addPlanetRange(group: THREE.Group, path: Path, len: number) {
  const colors = [0xffd166, 0x6ec5ff, 0x9b5cf6, 0xef476f];
  for (let i = 0; i < 7; i++) {
    const planet = new THREE.Mesh(
      new THREE.SphereGeometry(4 + n(i, 90) * 5, 18, 14),
      new THREE.MeshBasicMaterial({ color: colors[i % colors.length]!, fog: false }),
    );
    const s = n(i, 91) * len;
    const side = i % 2 === 0 ? -1 : 1;
    const pos = path.positionAt(s, side * (48 + n(i, 92) * 28));
    planet.position.set(pos.x, 18 + n(i, 93) * 18, pos.z);
    group.add(planet);
    if (i % 2 === 0) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(planet.geometry.parameters.radius * 1.35, 0.08, 8, 32),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7, fog: false }),
      );
      ring.rotation.x = Math.PI / 2.8;
      planet.add(ring);
    }
  }
}

type Bird = {
  mesh: THREE.Object3D;
  centerS: number;
  radius: number;
  phase: number;
  height: number;
  wingL: THREE.Object3D;
  wingR: THREE.Object3D;
  path: Path;
};

function addBird(group: THREE.Group, path: Path, len: number, i: number, speed: number): Bird {
  const colors = [0xef476f, 0xffd166, 0x06d6a0, 0x6ec5ff, 0x9b5cf6];
  const bird = new THREE.Group();
  const bodyMat = mat(colors[i % colors.length]!);
  bird.add(new THREE.Mesh(new SoftBox(0.4, 0.4, 0.7), bodyMat));
  const wingL = new THREE.Mesh(new SoftBox(0.7, 0.05, 0.4), bodyMat);
  wingL.position.x = -0.55;
  bird.add(wingL);
  const wingR = new THREE.Mesh(new SoftBox(0.7, 0.05, 0.4), bodyMat);
  wingR.position.x = 0.55;
  bird.add(wingR);
  group.add(bird);
  void speed;
  return {
    mesh: bird,
    centerS: 30 + (i / 10) * (len - 60) + n(i, 100) * 20,
    radius: 10 + n(i, 101) * 7,
    phase: n(i, 102) * Math.PI * 2,
    height: 10 + n(i, 103) * 5,
    wingL,
    wingR,
    path,
  };
}

function updateBirds(birds: Bird[], t: number) {
  for (const b of birds) {
    const angle = t * 0.45 + b.phase;
    const pos = b.path.positionAt(b.centerS + Math.cos(angle) * 4, Math.sin(angle) * b.radius);
    b.mesh.position.set(pos.x, b.height + Math.sin(t * 1.7 + b.phase), pos.z);
    b.mesh.rotation.y = angle + Math.PI / 2;
    const flap = Math.sin(t * 8 + b.phase) * 0.6;
    b.wingL.rotation.z = flap;
    b.wingR.rotation.z = -flap;
  }
}

function mat(color: THREE.ColorRepresentation) {
  return new THREE.MeshStandardMaterial({ color, roughness: 0.72 });
}

function n(a: number, b: number) {
  const v = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return v - Math.floor(v);
}
